import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  Paper,
  IconButton,
  useTheme,
  useMediaQuery,
  MobileStepper,
} from '@mui/material'
import {
  Close as CloseIcon,
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  PlayArrow as PlayIcon,
  EmojiEvents as TrophyIcon,
  MenuBook as MenuBookIcon,
  Quiz as QuizIcon,
  GroupAdd as GroupAddIcon,
  School as SchoolIcon,
} from '@mui/icons-material'

export interface TutorialStep {
  title: string
  description: string
  icon: React.ReactNode
  image?: string
}

interface TutorialModalProps {
  open: boolean
  onClose: () => void
  onComplete: () => void
  title: string
  steps: TutorialStep[]
}

export const TutorialModal: React.FC<TutorialModalProps> = ({
  open,
  onClose,
  onComplete,
  title,
  steps,
}) => {
  const { t } = useTranslation('tutorial')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [activeStep, setActiveStep] = useState(0)

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      onComplete()
    } else {
      setActiveStep((prev) => prev + 1)
    }
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  const handleClose = () => {
    setActiveStep(0)
    onClose()
  }

  const isLastStep = activeStep === steps.length - 1

  // モバイル向けシンプルビュー
  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            pb: 1,
          }}
        >
          <Typography variant="h6" component="span">
            {title}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              py: 2,
            }}
          >
            <Box
              sx={{
                bgcolor: 'primary.light',
                borderRadius: '50%',
                p: 2,
                mb: 3,
                color: 'primary.contrastText',
              }}
            >
              {steps[activeStep].icon}
            </Box>
            <Typography variant="h6" gutterBottom>
              {steps[activeStep].title}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ px: 2 }}>
              {steps[activeStep].description}
            </Typography>
          </Box>
        </DialogContent>

        <Box sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <MobileStepper
            variant="dots"
            steps={steps.length}
            position="static"
            activeStep={activeStep}
            sx={{ bgcolor: 'transparent', mb: 1 }}
            nextButton={<Box />}
            backButton={<Box />}
          />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<BackIcon />}
              sx={{ flex: 1 }}
            >
              {t('buttons.back')}
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={isLastStep ? <CheckCircleIcon /> : <NextIcon />}
              sx={{ flex: 1 }}
            >
              {isLastStep ? t('buttons.complete') : t('buttons.next')}
            </Button>
          </Box>
        </Box>
      </Dialog>
    )
  }

  // デスクトップ向けステッパービュー
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: 500 },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h5" component="span">
          {title}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', gap: 4 }}>
          {/* 左側：ステッパー */}
          <Box sx={{ width: 280, flexShrink: 0 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={index}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                          color: index <= activeStep ? 'primary.contrastText' : 'text.secondary',
                          fontSize: 14,
                        }}
                      >
                        {index < activeStep ? <CheckCircleIcon fontSize="small" /> : index + 1}
                      </Box>
                    )}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={index === activeStep ? 600 : 400}
                      color={index === activeStep ? 'primary' : 'text.secondary'}
                    >
                      {step.title}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* 右側：コンテンツ */}
          <Box sx={{ flex: 1 }}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                bgcolor: 'grey.50',
                borderRadius: 2,
                minHeight: 300,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  borderRadius: '50%',
                  p: 3,
                  mb: 3,
                  color: 'primary.contrastText',
                  '& .MuiSvgIcon-root': { fontSize: 48 },
                }}
              >
                {steps[activeStep].icon}
              </Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                {steps[activeStep].title}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                {steps[activeStep].description}
              </Typography>
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={handleClose} color="inherit">
          {t('buttons.skip')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={handleBack} disabled={activeStep === 0} startIcon={<BackIcon />}>
          {t('buttons.back')}
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          endIcon={isLastStep ? <CheckCircleIcon /> : <NextIcon />}
        >
          {isLastStep ? t('buttons.complete') : t('buttons.next')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// 理解度テストチュートリアル用のプリセット
export const useMyEnrollmentsTutorialSteps = (): TutorialStep[] => {
  const { t } = useTranslation('tutorial')

  return [
    {
      title: t('myEnrollments.step1.title'),
      description: t('myEnrollments.step1.description'),
      icon: <AssignmentIcon sx={{ fontSize: 32 }} />,
    },
    {
      title: t('myEnrollments.step2.title'),
      description: t('myEnrollments.step2.description'),
      icon: <ScheduleIcon sx={{ fontSize: 32 }} />,
    },
    {
      title: t('myEnrollments.step3.title'),
      description: t('myEnrollments.step3.description'),
      icon: <PlayIcon sx={{ fontSize: 32 }} />,
    },
    {
      title: t('myEnrollments.step4.title'),
      description: t('myEnrollments.step4.description'),
      icon: <TrophyIcon sx={{ fontSize: 32 }} />,
    },
  ]
}

// 管理者オンボーディングチュートリアル用のプリセット
export const useAdminOnboardingTutorialSteps = (): TutorialStep[] => {
  const { t } = useTranslation('tutorial')

  return [
    {
      title: t('adminOnboarding.step1.title'),
      description: t('adminOnboarding.step1.description'),
      icon: <MenuBookIcon sx={{ fontSize: 32 }} />,
    },
    {
      title: t('adminOnboarding.step2.title'),
      description: t('adminOnboarding.step2.description'),
      icon: <QuizIcon sx={{ fontSize: 32 }} />,
    },
    {
      title: t('adminOnboarding.step3.title'),
      description: t('adminOnboarding.step3.description'),
      icon: <GroupAddIcon sx={{ fontSize: 32 }} />,
    },
    {
      title: t('adminOnboarding.step4.title'),
      description: t('adminOnboarding.step4.description'),
      icon: <SchoolIcon sx={{ fontSize: 32 }} />,
    },
  ]
}
